import React, { useMemo, useRef } from "react";
import { Box } from "@mui/material";

interface Props {
  svg: string;
  width?: number | string;
  height?: number | string;
}

let uidCounter = 0;

/** Generate a unique ID for SVG element remapping */
export function nextSvgUid(): string {
  return `sd${++uidCounter}`;
}

/**
 * Sanitize an SVG string by remapping all internal IDs to unique values.
 * This prevents cross-SVG ID collisions (e.g. eraser masks bleeding between
 * drawings) when multiple SVGs are rendered in the same DOM.
 */
export function sanitizeSvg(raw: string, uid: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "image/svg+xml");
  const svgEl = doc.querySelector("svg");
  if (!svgEl) return "";

  // Remove dangerous elements
  const dangerous = svgEl.querySelectorAll("script, foreignObject, iframe, object, embed, link");
  dangerous.forEach((el) => el.remove());

  // Collect all IDs and remap them to unique values to prevent cross-SVG ID collisions
  // when multiple inline SVGs are on the same page (e.g. gallery grid).
  const idMap = new Map<string, string>();
  svgEl.querySelectorAll("[id]").forEach((el) => {
    const oldId = el.getAttribute("id")!;
    const newId = `${oldId}--${uid}`;
    idMap.set(oldId, newId);
    el.setAttribute("id", newId);
  });

  // Rewrite all ID references: href="#id", xlink:href="#id", and url(#id) attributes
  svgEl.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      // Remove event handlers and javascript: URLs
      if (attr.name.startsWith("on") || attr.value.includes("javascript:")) {
        el.removeAttribute(attr.name);
        return;
      }
      // Rewrite href / xlink:href fragment references
      if ((attr.name === "href" || attr.name === "xlink:href") && attr.value.startsWith("#")) {
        const oldId = attr.value.slice(1);
        if (idMap.has(oldId)) el.setAttribute(attr.name, `#${idMap.get(oldId)}`);
        return;
      }
      // Rewrite url(#id) references (mask, clip-path, fill, filter, etc.)
      const urlMatch = attr.value.match(/^url\(#(.+)\)$/);
      if (urlMatch && idMap.has(urlMatch[1])) {
        el.setAttribute(attr.name, `url(#${idMap.get(urlMatch[1])})`);
      }
    });
  });

  return svgEl.outerHTML;
}

export default function SvgDrawing({ svg, width = 200, height = 200 }: Props) {
  const uid = useRef(`sd${++uidCounter}`).current;
  const sanitized = useMemo(() => sanitizeSvg(svg, uid), [svg, uid]);

  if (!sanitized) return null;

  return (
    <Box
      sx={{
        width,
        height,
        overflow: "hidden",
        borderRadius: 2,
        "& svg": { width: "100%", height: "100%", display: "block" },
      }}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
